import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { AlertTriangle, Pill, FlaskConical, Scan, Scissors, Share2, DollarSign, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { apiErrorMessage } from '../lib/utils';

export default function ForceCloseModal({ open, onOpenChange, patientId, patientName, userRole, onSuccess }) {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [pendingTasks, setPendingTasks] = useState(null);
  const [remark, setRemark] = useState('');
  const [overrideBilling, setOverrideBilling] = useState(false);

  // Selected items
  const [selectedPrescriptions, setSelectedPrescriptions] = useState([]);
  const [selectedLabTests, setSelectedLabTests] = useState([]);
  const [selectedImagingTests, setSelectedImagingTests] = useState([]);
  const [selectedProcedures, setSelectedProcedures] = useState([]);
  const [selectedReferrals, setSelectedReferrals] = useState([]);

  const fetchPendingTasks = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/patients/${patientId}/pending-tasks`);
      const data = response.data;
      setPendingTasks(data);
      // Pre-select all pending items
      setSelectedPrescriptions(data.pending_prescriptions.map(p => p.id));
      setSelectedLabTests(data.pending_lab_tests.map(t => t.id));
      setSelectedImagingTests(data.pending_imaging_tests.map(t => t.id));
      setSelectedProcedures(data.pending_procedures.map(p => p.id));
      setSelectedReferrals(data.pending_referrals.map(r => r.id));
    } catch (error) {
      toast.error('Failed to load pending tasks');
    } finally {
      setLoading(false);
    }
  }, [patientId]);


  useEffect(() => {
    if (open && patientId) {
      fetchPendingTasks();
    }
  }, [open, patientId, fetchPendingTasks]);
  const toggleItem = (list, setList, id) => {
    setList(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleForceClose = async () => {
    if (remark.trim().length < 10) {
      toast.error('Remark must be at least 10 characters');
      return;
    }

    if (pendingTasks?.total_outstanding > 0 && !overrideBilling && !['admin', 'super_admin'].includes(userRole)) {
      toast.error('Outstanding bills exist. Only admin can override billing restriction.');
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post(`/patients/${patientId}/force-close`, {
        remark: remark.trim(),
        override_billing: overrideBilling,
        selected_prescriptions: selectedPrescriptions,
        selected_lab_tests: selectedLabTests,
        selected_imaging_tests: selectedImagingTests,
        selected_procedures: selectedProcedures,
        selected_referrals: selectedReferrals,
      });
      toast.success(response.data.message || 'Case closed with opt-out');
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to force close case'));
    } finally {
      setSubmitting(false);
    }
  };

  const totalSelected = selectedPrescriptions.length + selectedLabTests.length +
    selectedImagingTests.length + selectedProcedures.length + selectedReferrals.length;

  const renderTaskGroup = (title, icon, items, selected, setSelected) => {
    if (!items || items.length === 0) return null;
    const Icon = icon;
    return (
      <div className="space-y-2" data-testid={`task-group-${title.toLowerCase().replace(/\s/g, '-')}`}>
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          <Icon className="w-4 h-4" />
          {title} ({items.length})
        </div>
        <div className="space-y-1 pl-6">
          {items.map(item => (
            <div key={item.id} className="flex items-center gap-2">
              <Checkbox
                id={`task-${item.id}`}
                checked={selected.includes(item.id)}
                onCheckedChange={() => toggleItem(selected, setSelected, item.id)}
                data-testid={`task-checkbox-${item.id}`}
              />
              <Label htmlFor={`task-${item.id}`} className="text-sm cursor-pointer flex-1">
                {item.label || item.id}
              </Label>
              <Badge variant="outline" className="text-xs capitalize">{item.status}</Badge>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="force-close-modal">
        <DialogHeader>
          <DialogTitle className="font-[Manrope] flex items-center gap-2 text-red-600">
            <ShieldAlert className="w-5 h-5" />
            Force Close / Patient Opt-Out
          </DialogTitle>
        </DialogHeader>

        {/* Warning Banner */}
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 flex items-start gap-2" data-testid="opt-out-warning">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-semibold">Patient has declined pending services.</p>
            <p className="mt-1">This will mark selected pending tasks as "Opted Out" and close the case. Historical records will be preserved.</p>
          </div>
        </div>

        {loading ? (
          <div className="py-8 text-center text-slate-500">Loading pending tasks...</div>
        ) : pendingTasks && (
          <div className="space-y-4">
            {/* Patient Info */}
            <div className="text-sm text-slate-600">
              Patient: <span className="font-semibold text-slate-900">{patientName}</span>
            </div>

            {/* Pending Tasks */}
            {!pendingTasks.has_pending_tasks ? (
              <div className="text-sm text-emerald-600 bg-emerald-50 p-3 rounded-lg">
                No pending tasks found. Case can be closed normally.
              </div>
            ) : (
              <div className="space-y-3 border rounded-lg p-3 bg-slate-50">
                <p className="text-sm font-semibold text-slate-700">Select pending items to opt-out:</p>
                {renderTaskGroup('Pharmacy / Prescriptions', Pill, pendingTasks.pending_prescriptions, selectedPrescriptions, setSelectedPrescriptions)}
                {renderTaskGroup('Lab Tests', FlaskConical, pendingTasks.pending_lab_tests, selectedLabTests, setSelectedLabTests)}
                {renderTaskGroup('Imaging Tests', Scan, pendingTasks.pending_imaging_tests, selectedImagingTests, setSelectedImagingTests)}
                {renderTaskGroup('Procedures', Scissors, pendingTasks.pending_procedures, selectedProcedures, setSelectedProcedures)}
                {renderTaskGroup('Referrals', Share2, pendingTasks.pending_referrals, selectedReferrals, setSelectedReferrals)}
              </div>
            )}

            {/* Outstanding Bills Warning */}
            {pendingTasks.total_outstanding > 0 && (
              <div className="bg-red-50 border border-red-300 rounded-lg p-3 space-y-2" data-testid="billing-warning">
                <div className="flex items-center gap-2 text-red-700 text-sm font-semibold">
                  <DollarSign className="w-4 h-4" />
                  Outstanding Bills: ₹{pendingTasks.total_outstanding.toLocaleString()}
                </div>
                {['admin', 'super_admin'].includes(userRole) ? (
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="override-billing"
                      checked={overrideBilling}
                      onCheckedChange={setOverrideBilling}
                      data-testid="override-billing-checkbox"
                    />
                    <Label htmlFor="override-billing" className="text-sm cursor-pointer text-red-700">
                      Override billing restriction (Admin only)
                    </Label>
                  </div>
                ) : (
                  <p className="text-xs text-red-600">Closure blocked due to outstanding dues. Contact admin to override.</p>
                )}
              </div>
            )}

            {/* Mandatory Remark */}
            <div className="space-y-2">
              <Label htmlFor="closure-remark" className="font-semibold">
                Reason / Remark <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="closure-remark"
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
                placeholder="e.g., Patient opted to purchase medicines externally, Patient declined further tests..."
                rows={3}
                className={remark.trim().length > 0 && remark.trim().length < 10 ? 'border-red-400' : ''}
                data-testid="closure-remark-input"
              />
              {remark.trim().length > 0 && remark.trim().length < 10 && (
                <p className="text-xs text-red-500">Minimum 10 characters required ({remark.trim().length}/10)</p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 justify-end pt-2 border-t">
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cancel-force-close-btn">
                Cancel
              </Button>
              <Button
                onClick={handleForceClose}
                disabled={submitting || remark.trim().length < 10 || (pendingTasks.total_outstanding > 0 && !overrideBilling && !['admin', 'super_admin'].includes(userRole))}
                className="bg-red-500 hover:bg-red-600"
                data-testid="confirm-force-close-btn"
              >
                {submitting ? 'Closing...' : `Force Close Case${totalSelected > 0 ? ` (${totalSelected} items)` : ''}`}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
